import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosInstance';
import { getApiBase } from '../utils/useApiBase';

const TYPES = [
  { value: 'imfashanyo', label: 'Imfashanyo' },
  { value: 'inguzanyo', label: 'Inguzanyo' },
  { value: 'inkunga_ya_leta', label: "Inkunga ya Leta" },
  { value: 'inkunga_bwite', label: 'Inkunga Bwite' },
  { value: 'ibindi', label: 'Ibindi' },
];

const empty = { izina: '', ubwoko: '', agaciro: '', itariki: '', citizen_id: '' };

const css = `
  @keyframes inkModalIn {
    from { opacity: 0; transform: scale(0.97) translateY(10px); }
    to   { opacity: 1; transform: scale(1)   translateY(0); }
  }
  @keyframes inkOverlayIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes inkShake {
    0%,100% { transform: translateX(0); }
    20%,60% { transform: translateX(-5px); }
    40%,80% { transform: translateX(5px); }
  }
  @keyframes inkSpin {
    to { transform: rotate(360deg); }
  }
  .ink-input {
    width: 100%;
    box-sizing: border-box;
    padding: 10px 14px 10px 40px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #0B1F3B;
    font-size: 0.9rem;
    font-family: inherit;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    -webkit-appearance: none;
  }
  .ink-input::placeholder { color: #94a3b8; }
  .ink-input:focus {
    border-color: #0B3D91;
    box-shadow: 0 0 0 3px rgba(11,61,145,0.15);
  }
  .ink-input.err {
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220,38,38,0.12);
    animation: inkShake 0.35s ease;
  }
  .ink-btn-cancel {
    padding: 10px 22px;
    background: transparent;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.2s;
  }
  .ink-btn-cancel:hover {
    background: #f1f5f9;
    color: #0B1F3B;
    border-color: #cbd5e1;
  }
  .ink-btn-save {
    padding: 10px 26px;
    background: #0B3D91;
    border: none;
    border-radius: 10px;
    color: #fff;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 130px;
    justify-content: center;
  }
  .ink-btn-save:hover:not(:disabled) {
    background: #093278;
  }
  .ink-btn-save:disabled { opacity: 0.6; cursor: not-allowed; }
  .ink-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  @media (max-width: 600px) {
    .ink-grid { grid-template-columns: 1fr; }
    .ink-footer { flex-direction: column-reverse !important; }
    .ink-btn-cancel, .ink-btn-save { width: 100%; justify-content: center; }
  }
`;

function FieldIcon({ icon }) {
  return (
    <i className={`bi ${icon}`} style={{
      position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
      color: '#0B3D91', fontSize: '0.9rem', pointerEvents: 'none',
    }} />
  );
}

function Field({ label, required, icon, error, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{
        fontSize: '0.75rem', fontWeight: 700, color: '#475569',
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {label}{required && <span style={{ color: '#dc2626', marginLeft: 3 }}>*</span>}
      </label>
      <div style={{ position: 'relative' }}>
        {icon && <FieldIcon icon={icon} />}
        {children}
      </div>
      {error && (
        <span style={{ color: '#dc2626', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className="bi bi-exclamation-circle-fill" /> {error}
        </span>
      )}
    </div>
  );
}

export default function InkungaModal({ show, onHide, onSave, editData = null }) {
  const isEdit = !!editData;
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);

  // Citizens already registered under Abaturage, for the auto-populate
  // dropdown instead of hand-typing a name.
  const [citizens, setCitizens] = useState([]);
  useEffect(() => {
    axios.get(`${getApiBase()}/isibo/citizens`)
      .then((res) => setCitizens(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCitizens([]));
  }, []);

  useEffect(() => {
    if (show) {
      setForm(editData
        ? {
            izina: editData.umuturage || '',
            ubwoko: editData.ubwoko || '',
            agaciro: editData.amount || '',
            itariki: editData.itariki?.slice(0,10) || '',
            citizen_id: editData.citizen_id || '',
          }
        : empty
      );
      setErrors({}); setSaving(false);
      requestAnimationFrame(() => setVisible(true));
    } else setVisible(false);
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const h = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [show]);

  const set = (k) => (e) => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors(er => ({ ...er, [k]: '' }));
  };

  const handleCitizenSelect = (e) => {
    const citizenId = e.target.value;
    const citizen = citizens.find((c) => String(c.umuturage_id) === String(citizenId));
    setForm((prev) => ({
      ...prev,
      citizen_id: citizenId,
      izina: citizen ? citizen.full_name : '',
    }));
    if (errors.izina) setErrors((prev) => ({ ...prev, izina: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.izina.trim())   e.izina   = 'Injiza izina ry\'umuturage';
    if (!form.ubwoko)         e.ubwoko  = 'Hitamo ubwoko bw\'inkunga';
    if (!form.agaciro || isNaN(form.agaciro) || +form.agaciro <= 0)
                              e.agaciro = 'Injiza agaciro nyacyo (RWF)';
    if (!form.itariki)        e.itariki = 'Hitamo itariki';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await onSave?.(form);
      handleClose();
    } catch {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onHide?.(), 220);
  };

  if (!show) return null;

  return (
    <>
      <style>{css}</style>
      {/* Overlay */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 1050,
          background: 'rgba(15,23,42,0.55)',
          animation: 'inkOverlayIn 0.2s ease forwards',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}
      >
        {/* Modal */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%', maxWidth: 760,
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 16,
            boxShadow: '0 20px 50px rgba(15,23,42,0.25)',
            overflow: 'hidden',
            animation: visible ? 'inkModalIn 0.22s cubic-bezier(0.175,0.885,0.32,1.1) forwards' : 'none',
            opacity: visible ? 1 : 0,
            maxHeight: '92dvh',
            display: 'flex', flexDirection: 'column',
          }}
        >
          {/* ── Header ── */}
          <div style={{
            background: 'linear-gradient(135deg, #0B3D91 0%, #093278 100%)',
            padding: '18px 22px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.1rem', color: '#fff',
                flexShrink: 0,
              }}><i className="bi bi-gift-fill" /></div>
              <div>
                <h2 style={{ margin: 0, color: '#fff', fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.01em' }}>
                  {isEdit ? 'Hindura Inkunga' : "Andika Inkunga Nshya"}
                </h2>
                <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.7)', fontSize: '0.76rem' }}>
                  Inkunga za Leta · Gucunga abaturage
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.85rem', transition: 'background 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
              aria-label="Funga"
            ><i className="bi bi-x-lg" /></button>
          </div>

          {/* ── Info Banner ── */}
          <div style={{
            margin: '16px 22px 0',
            padding: '11px 16px',
            background: 'rgba(11,61,145,0.06)',
            border: '1px solid rgba(11,61,145,0.15)',
            borderRadius: 10,
            display: 'flex', alignItems: 'center', gap: 10,
            flexShrink: 0,
          }}>
            <i className="bi bi-info-circle-fill" style={{ color: '#0B3D91', fontSize: '1rem', flexShrink: 0 }} />
            <p style={{ margin: 0, color: '#334155', fontSize: '0.82rem', lineHeight: 1.5 }}>
              Uzuza neza amakuru yose ashizweho inyenyeri (<span style={{ color: '#dc2626' }}>*</span>) ni ngombwa
            </p>
          </div>

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} style={{ padding: '20px 22px 22px', overflowY: 'auto', flex: 1 }}>
            <div className="ink-grid">
              {/* Izina */}
              <Field label="Izina ry'umuturage" required icon="bi-person-fill" error={errors.izina}>
                <select
                  className={`ink-input${errors.izina ? ' err' : ''}`}
                  value={form.citizen_id || ''}
                  onChange={handleCitizenSelect}
                  style={{ cursor: 'pointer' }}
                  autoFocus
                >
                  <option value="">-- Hitamo umuturage --</option>
                  {citizens.map((c) => (
                    <option key={c.umuturage_id} value={c.umuturage_id}>{c.full_name}</option>
                  ))}
                </select>
              </Field>

              {/* Ubwoko */}
              <Field label="Ubwoko bw'inkunga" required icon="bi-list-check" error={errors.ubwoko}>
                <select
                  className={`ink-input${errors.ubwoko ? ' err' : ''}`}
                  value={form.ubwoko}
                  onChange={set('ubwoko')}
                  style={{ cursor: 'pointer' }}
                >
                  <option value="">Hitamo ubwoko...</option>
                  {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>

              {/* Agaciro */}
              <Field label="Agaciro (RWF)" required icon="bi-cash-stack" error={errors.agaciro}>
                <input
                  className={`ink-input${errors.agaciro ? ' err' : ''}`}
                  type="number"
                  min="1"
                  placeholder="0"
                  value={form.agaciro}
                  onChange={set('agaciro')}
                />
              </Field>

              {/* Itariki */}
              <Field label="Itariki" required icon="bi-calendar3" error={errors.itariki}>
                <input
                  className={`ink-input${errors.itariki ? ' err' : ''}`}
                  type="date"
                  value={form.itariki}
                  onChange={set('itariki')}
                />
              </Field>
            </div>

            {/* ── Footer ── */}
            <div className="ink-footer" style={{
              display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24,
            }}>
              <button type="button" className="ink-btn-cancel" onClick={handleClose}>
                Hagarika
              </button>
              <button type="submit" className="ink-btn-save" disabled={saving}>
                {saving
                  ? <><span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'inkSpin 0.7s linear infinite', display: 'inline-block' }} /> Birabikwa...</>
                  : <><i className={`bi ${isEdit ? 'bi-check2' : 'bi-save2'}`} /> {isEdit ? 'Vugurura' : 'Bika Inkunga'}</>
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
